import { useEffect, useState } from 'react';
import { useStore } from '../store/useStore.js';

const STORAGE_KEY = 'synapse_onboarding_v1';

function isDismissed(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}
function dismiss() {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
}

export function CanvasOnboarding({ onAdd }: { onAdd: () => void }) {
  const nodes = useStore((s) => s.nodes);
  const links = useStore((s) => s.links);
  const [dismissed, setDismissed] = useState(isDismissed);
  const [showLinkTip, setShowLinkTip] = useState(false);

  // Tooltip: after first node, show linking hint.
  useEffect(() => {
    if (dismissed) return;
    if (nodes.length === 1 && links.length === 0) {
      setShowLinkTip(true);
      const t = setTimeout(() => setShowLinkTip(false), 6000);
      return () => clearTimeout(t);
    }
    if (nodes.length >= 3 && links.length === 0) {
      setShowLinkTip(true);
      const t = setTimeout(() => setShowLinkTip(false), 6000);
      return () => clearTimeout(t);
    }
  }, [nodes.length, links.length, dismissed]);

  // Empty state — no nodes.
  if (nodes.length === 0 && !dismissed) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 'var(--topbar-h)',
          left: 0, right: 0, bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 50,
        }}
      >
        <div
          style={{
            pointerEvents: 'all',
            textAlign: 'center',
            padding: '40px 48px',
            background: 'rgba(2, 6, 20, 0.72)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(0,245,255,0.12)',
            borderRadius: 20,
            maxWidth: 420,
          }}
        >
          <div style={{
            fontSize: 40,
            marginBottom: 16,
            filter: 'drop-shadow(0 0 16px var(--neon))',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--neon)' }}>
              hub
            </span>
          </div>
          <div style={{
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 8,
            background: 'linear-gradient(135deg, var(--neon), var(--violet))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Comece pelo primeiro nó
          </div>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 28, lineHeight: 1.6 }}>
            Crie ideias, conecte-as e navegue entre universos de conhecimento.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => { onAdd(); dismiss(); setDismissed(true); }}
              style={{
                background: 'var(--neon)', color: '#0a0a0f',
                border: 'none', borderRadius: 10,
                padding: '11px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                boxShadow: '0 0 24px rgba(0,245,255,0.4)',
                transition: 'transform 0.15s ease',
              }}
            >
              Criar primeiro nó
            </button>
            <button
              onClick={() => { dismiss(); setDismissed(true); }}
              style={{
                background: 'transparent', color: 'var(--muted)',
                border: '1px solid var(--border)', borderRadius: 10,
                padding: '11px 20px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Já sei usar
            </button>
          </div>
          <p style={{ marginTop: 18, fontSize: 11, color: 'var(--hint)' }}>
            Dica: pressione <kbd style={kbdStyle}>N</kbd> para criar um nó rapidamente
          </p>
        </div>
      </div>
    );
  }

  // Linking tip floating toast.
  if (showLinkTip) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 90,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(2, 6, 20, 0.88)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(0,245,255,0.2)',
          borderRadius: 10,
          padding: '10px 18px',
          fontSize: 13,
          color: 'var(--text)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
          animation: 'fadeInUp 0.3s ease',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: 'var(--neon)' }}>
          link
        </span>
        Clique nos handles <strong style={{ color: 'var(--neon)' }}>⊕</strong> do nó ou use{' '}
        <kbd style={kbdStyle}>Shift</kbd> + arrastar para conectar ideias
      </div>
    );
  }

  return null;
}

const kbdStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 11,
  fontFamily: 'monospace',
};
